import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { ScoreGauge } from "@/components/common/ScoreGauge";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { fmtDate, RAG, scoreRag } from "@/lib/format";
import { toast } from "sonner";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, Legend,
} from "recharts";

const SCALE = [
  { v: 1, label: "Significant weakness" },
  { v: 2, label: "Needs improvement" },
  { v: 3, label: "Developing" },
  { v: 4, label: "Performing well" },
  { v: 5, label: "High-performing" },
];

export default function BusinessHealth() {
  const { orgId } = useOrg();
  const { canEdit } = useAuth();
  const [latest, setLatest] = useState(null);
  const [mode, setMode] = useState("view"); // view | assess

  const load = () => api.get(`/assessments/latest?org_id=${orgId}`).then((r) => setLatest(r.data));
  useEffect(() => { if (orgId) { setLatest(null); setMode("view"); load(); } }, [orgId]);

  if (!latest) return <PageLoading />;

  if (mode === "assess") {
    return <AssessmentFlow orgId={orgId} onDone={() => { setMode("view"); load(); }} onCancel={() => setMode("view")} />;
  }

  const a = latest.assessment;
  const prev = latest.previous;

  return (
    <div data-testid="business-health-page">
      <PageHeader
        eyebrow="Diagnostics"
        title="Business Health"
        subtitle="A structured view of performance across ten core areas of the business."
        icon="Activity"
        actions={canEdit && (
          <Button onClick={() => setMode("assess")} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="take-assessment-btn">
            <Icons.ClipboardCheck size={16} className="mr-1.5" /> {a ? "New assessment" : "Take assessment"}
          </Button>
        )}
      />

      {!a ? (
        <EmptyState icon="Activity" title="No assessment completed" description="Complete a Business Health Assessment to see your score, radar and risks."
          action={canEdit && <Button onClick={() => setMode("assess")} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]">Start assessment</Button>} />
      ) : (
        <div className="space-y-5">
          {/* Top row */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <div className="amden-card flex flex-col items-center justify-center p-6">
              <ScoreGauge score={a.results.overall} size={190} />
              {typeof a.results.movement === "number" && (
                <div className="mt-3 flex items-center gap-1.5 text-sm">
                  {a.results.movement >= 0 ? <Icons.TrendingUp size={15} className="text-[#3D8B65]" /> : <Icons.TrendingDown size={15} className="text-[#C95555]" />}
                  <span className={a.results.movement >= 0 ? "text-[#3D8B65]" : "text-[#C95555]"}>{a.results.movement >= 0 ? "+" : ""}{a.results.movement} pts vs previous</span>
                </div>
              )}
              <div className="mt-1 text-xs text-[#1E2D44]/45">Assessed {fmtDate(a.created_at)}</div>
            </div>

            <div className="amden-card p-6 lg:col-span-2">
              <h3 className="mb-2 font-display text-base font-bold">Performance radar</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={a.results.areas.map((x) => ({ area: x.name.split(" ")[0], score: x.score, prev: prev?.results.areas.find((p) => p.key === x.key)?.score }))}>
                    <PolarGrid stroke="#E4E8EF" />
                    <PolarAngleAxis dataKey="area" tick={{ fontSize: 10, fill: "#1E2D44" }} />
                    {prev && <Radar name="Previous" dataKey="prev" stroke="#8A97AB" fill="#8A97AB" fillOpacity={0.15} />}
                    <Radar name="Current" dataKey="score" stroke="#FFB000" fill="#FFB000" fillOpacity={0.35} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E4E8EF", fontSize: 12 }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Strengths / risks */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <InfoList title="Strongest areas" icon="Award" tone="green" items={a.results.strongest} />
            <InfoList title="Weakest areas" icon="TrendingDown" tone="red" items={a.results.weakest} />
            <InfoList title="Recommended priorities" icon="Flag" tone="amber" items={a.results.recommended_priorities} empty="No urgent priorities — keep building." />
          </div>

          {/* Area breakdown */}
          <div className="amden-card p-6">
            <h3 className="mb-4 font-display text-base font-bold">Area breakdown</h3>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={a.results.areas} margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: "#8A97AB" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11, fill: "#1E2D44" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E4E8EF", fontSize: 12 }} cursor={{ fill: "#F4F6F9" }} />
                  <Bar dataKey="score" radius={[0, 6, 6, 0]} barSize={16}>
                    {a.results.areas.map((x, i) => <Cell key={i} fill={RAG[scoreRag(x.score)].solid} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Comparison table */}
          {prev && (
            <div className="amden-card p-6">
              <h3 className="mb-4 font-display text-base font-bold">Previous vs current</h3>
              <div className="space-y-2">
                {a.results.areas.map((x) => {
                  const p = prev.results.areas.find((y) => y.key === x.key)?.score ?? 0;
                  const delta = x.score - p;
                  return (
                    <div key={x.key} className="flex items-center gap-3">
                      <span className="w-44 shrink-0 text-sm text-[#1E2D44]/70">{x.name}</span>
                      <div className="flex-1"><Progress value={x.score} className="h-2" /></div>
                      <span className="w-12 text-right text-sm font-medium">{x.score}</span>
                      <span className={`w-12 text-right text-xs font-semibold ${delta > 0 ? "text-[#3D8B65]" : delta < 0 ? "text-[#C95555]" : "text-[#1E2D44]/40"}`}>
                        {delta > 0 ? "+" : ""}{delta}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoList({ title, icon, tone, items, empty }) {
  const Icon = Icons[icon];
  return (
    <div className="amden-card p-6">
      <div className="mb-3 flex items-center gap-2">
        <Icon size={16} className={RAG[tone].text} />
        <h3 className="font-display text-sm font-bold">{title}</h3>
      </div>
      {items && items.length ? (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-[#1E2D44]/75">
              <span className={`h-1.5 w-1.5 rounded-full ${RAG[tone].dot}`} />{it}
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-[#1E2D44]/40">{empty || "—"}</p>}
    </div>
  );
}

function AssessmentFlow({ orgId, onDone, onCancel }) {
  const [template, setTemplate] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/assessment/template").then((r) => setTemplate(r.data)); }, []);
  if (!template) return <PageLoading />;

  const sections = template.sections;
  const section = sections[step];
  const answeredInSection = section.questions.filter((q) => answers[q.id]).length;
  const totalAnswered = Object.keys(answers).length;
  const totalQuestions = sections.reduce((s, sec) => s + sec.questions.length, 0);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/assessments?org_id=${orgId}`, { answers, comments, period: "Quarterly" });
      toast.success("Assessment completed");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const isLast = step === sections.length - 1;

  return (
    <div data-testid="assessment-flow">
      <button onClick={onCancel} className="mb-4 flex items-center gap-1.5 text-sm text-[#1E2D44]/55 hover:text-[#FFB000]"><Icons.ArrowLeft size={15} /> Cancel</button>
      <div className="mb-5">
        <div className="label-eyebrow mb-1">Business Health Assessment · Section {step + 1} of {sections.length}</div>
        <h1 className="font-display text-2xl font-bold text-[#0B1729]">{section.name}</h1>
        <div className="mt-3"><Progress value={(totalAnswered / totalQuestions) * 100} className="h-2" /></div>
        <div className="mt-1.5 text-xs text-[#1E2D44]/50">{totalAnswered} of {totalQuestions} questions answered</div>
      </div>

      <div className="space-y-4">
        {section.questions.map((q, i) => (
          <div key={q.id} className="amden-card p-5" data-testid={`question-${q.id}`}>
            <div className="mb-3 text-sm font-medium text-[#0B1729]">{i + 1}. {q.text}</div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {SCALE.map((s) => {
                const active = answers[q.id] === s.v;
                return (
                  <button
                    key={s.v}
                    onClick={() => setAnswers({ ...answers, [q.id]: s.v })}
                    data-testid={`answer-${q.id}-${s.v}`}
                    className="flex flex-col items-center gap-1 rounded-lg border p-2.5 text-center transition-all"
                    style={{ borderColor: active ? "#FFB000" : "#E4E8EF", background: active ? "#FFB000" : "#fff", color: active ? "#fff" : "#1E2D44" }}
                  >
                    <span className="font-display text-lg font-bold">{s.v}</span>
                    <span className="text-[10px] leading-tight">{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div className="amden-card p-5">
          <Label className="text-sm">Comments for {section.name} (optional)</Label>
          <Textarea value={comments[section.key] || ""} onChange={(e) => setComments({ ...comments, [section.key]: e.target.value })} className="mt-2" placeholder="Add context or evidence…" />
        </div>
      </div>

      <div className="sticky bottom-20 mt-6 flex items-center justify-between rounded-xl border border-[#E4E8EF] bg-white/90 p-3 backdrop-blur lg:bottom-4">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>Back</Button>
        <span className="text-xs text-[#1E2D44]/50">{answeredInSection}/{section.questions.length} in this section</span>
        {isLast ? (
          <Button onClick={submit} disabled={saving || totalAnswered === 0} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="submit-assessment-btn">
            {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Submit & score"}
          </Button>
        ) : (
          <Button onClick={() => setStep((s) => s + 1)} className="bg-[#0B1729] text-white hover:bg-[#1E2D44]" data-testid="next-section-btn">Next section</Button>
        )}
      </div>
    </div>
  );
}
